import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { createApiClient } from '../../services/api/client'
import {
  contactSearchResponseSchema,
  contactSelectionResponseSchema,
  startConversationResponseSchema,
  type ContactSearchResponse,
  type ContactSelection,
  type StartConversationResult,
} from './contact-directory-contracts'

export type ContactDirectoryRepository = {
  search: (
    tenantId: string,
    query: string,
    cursor?: string
  ) => Promise<ContactSearchResponse>
  select: (
    tenantId: string,
    contactRef: string,
    idempotencyKey: string
  ) => Promise<ContactSelection>
  start?: (
    tenantId: string,
    selectionRef: string,
    idempotencyKey: string,
    content: string
  ) => Promise<StartConversationResult>
  startStatus?: (
    tenantId: string,
    idempotencyKey: string
  ) => Promise<StartConversationResult>
  recoverStart?: (
    tenantId: string,
    startCommandRef: string
  ) => Promise<StartConversationResult>
}

export async function executeStartConversation(
  repository: ContactDirectoryRepository,
  tenantId: string,
  selectionRef: string,
  idempotencyKey: string,
  content: string
) {
  if (!repository.start || !repository.startStatus) {
    throw new Error('START_CONVERSATION_UNAVAILABLE')
  }
  let result: StartConversationResult
  try {
    result = await repository.start(
      tenantId,
      selectionRef,
      idempotencyKey,
      content
    )
  } catch {
    result = await repository.startStatus(tenantId, idempotencyKey)
  }
  if (result.status === 'RECONCILIATION_REQUIRED' && repository.recoverStart) {
    return repository.recoverStart(tenantId, result.startCommandRef)
  }
  return result
}

export function createContactDirectoryRepository({
  getUser,
  baseUrl = getEnvironment().userManagementBaseUrl,
  fetchImplementation,
}: {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}): ContactDirectoryRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })
  const root = (tenantId: string) =>
    `/dashboard/clinics/${encodeURIComponent(tenantId)}/contacts`
  return {
    search(tenantId, query, cursor) {
      const parameters = new URLSearchParams({ pageSize: '20' })
      if (cursor) parameters.set('cursor', cursor)
      return client.request(`${root(tenantId)}/search?${parameters}`, {
        method: 'GET',
        cache: 'no-store',
        headers: { 'X-Flio-Contact-Query': query },
        responseSchema: contactSearchResponseSchema,
      })
    },
    select(tenantId, contactRef, idempotencyKey) {
      return client.request(`${root(tenantId)}/selection`, {
        method: 'POST',
        body: { contactRef, idempotencyKey },
        responseSchema: contactSelectionResponseSchema,
      })
    },
    start(tenantId, selectionRef, idempotencyKey, content) {
      return client.request(
        `/dashboard/clinics/${encodeURIComponent(tenantId)}/start-conversations`,
        {
          method: 'POST',
          body: { selectionRef, idempotencyKey, content },
          responseSchema: startConversationResponseSchema,
        }
      )
    },
    startStatus(tenantId, idempotencyKey) {
      return client.request(
        `/dashboard/clinics/${encodeURIComponent(tenantId)}/start-conversations/status`,
        {
          method: 'POST',
          body: { idempotencyKey },
          responseSchema: startConversationResponseSchema,
        }
      )
    },
    recoverStart(tenantId, startCommandRef) {
      return client.request(
        `/dashboard/clinics/${encodeURIComponent(tenantId)}/start-conversations/recovery`,
        {
          method: 'POST',
          body: { startCommandRef },
          responseSchema: startConversationResponseSchema,
        }
      )
    },
  }
}
